import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Target,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  BarChart3,
  PieChart,
  Activity,
  Plus,
  Eye,
  ArrowRight,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  ShoppingCart,
  Truck,
  UserCheck,
  Star,
  Zap,
  Award,
  Building,
  MapPin,
  Package,
  UserCheck2,
  IndianRupee
} from 'lucide-react';
import Button from '../../components/common/Button/Button';

const CRMSalesDashboard = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedRegion, setSelectedRegion] = useState('all');

  // Mock data for CRM Sales dashboard
  const salesStats = [
    {
      title: 'Revenue',
      value: '₹12,45,67,890',
      change: '+18.2%',
      changeType: 'positive',
      icon: IndianRupee,
      color: 'bg-green-500'
    },
    {
      title: 'Leads',
      value: '8,247',
      change: '+15.8%',
      changeType: 'positive',
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      title: 'Conversion Rate',
      value: '24.3%',
      change: '+2.1%',
      changeType: 'positive',
      icon: Target,
      color: 'bg-purple-500'
    },
    {
      title: 'Pipeline Value',
      value: '₹18,45,67,000',
      change: '+22.7%',
      changeType: 'positive',
      icon: TrendingUp,
      color: 'bg-orange-500'
    },
    {
      title: 'Units Sold',
      value: '15,847',
      change: '+28.5%',
      changeType: 'positive',
      icon: Package,
      color: 'bg-indigo-500'
    },
    {
      title: 'Number of Buyers',
      value: '2,156',
      change: '+19.3%',
      changeType: 'positive',
      icon: UserCheck2,
      color: 'bg-teal-500'
    },
    {
      title: 'Units per Buyer',
      value: '7.3',
      change: '+8.2%',
      changeType: 'positive',
      icon: TrendingUp,
      color: 'bg-cyan-500'
    }
  ];

  const recentActivities = [
    {
      id: 1,
      type: 'lead',
      title: 'New Lead: TechCorp Solutions',
      description: 'High-value enterprise lead from website',
      time: '2 hours ago',
      status: 'new',
      value: '₹2,50,000'
    },
    {
      id: 2,
      type: 'opportunity',
      title: 'Opportunity: Global Manufacturing',
      description: 'Proposal sent for manufacturing software',
      time: '4 hours ago',
      status: 'proposal',
      value: '₹5,00,000'
    },
    {
      id: 3,
      type: 'deal',
      title: 'Deal Closed: Retail Chain',
      description: 'Successfully closed retail management deal',
      time: '1 day ago',
      status: 'closed',
      value: '₹1,20,000'
    },
    {
      id: 4,
      type: 'followup',
      title: 'Follow-up: Healthcare Provider',
      description: 'Scheduled follow-up call for next week',
      time: '2 days ago',
      status: 'scheduled',
      value: '₹3,00,000'
    }
  ];

  const topPerformers = [
    {
      name: 'John Smith',
      role: 'Sales Manager',
      target: 1000000,
      achieved: 850000,
      achievement: 85,
      leads: 45,
      deals: 12
    },
    {
      name: 'Sarah Johnson',
      role: 'Sales Executive',
      target: 750000,
      achieved: 720000,
      achievement: 96,
      leads: 38,
      deals: 15
    },
    {
      name: 'Mike Wilson',
      role: 'Sales Executive',
      target: 750000,
      achieved: 680000,
      achievement: 91,
      leads: 42,
      deals: 11
    }
  ];

  const quickActions = [
    {
      title: 'Create Lead',
      description: 'Add new lead to the system',
      icon: Plus,
      color: 'bg-blue-500',
      onClick: () => navigate('/lead_sources_capture')
    },
    {
      title: 'View Pipeline',
      description: 'Check opportunity pipeline',
      icon: Target,
      color: 'bg-green-500',
      onClick: () => navigate('/Opportunity-dashboard')
    },
    {
      title: 'Generate Quote',
      description: 'Create new quotation',
      icon: FileText,
      color: 'bg-purple-500',
      onClick: () => navigate('/quote_builder')
    },
    {
      title: 'View Reports',
      description: 'Access sales analytics',
      icon: BarChart3,
      color: 'bg-orange-500',
      onClick: () => navigate('/sales_dashboard')
    }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600">Comprehensive view of sales performance and activities</p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>

          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="all">All Regions</option>
            <option value="north">North</option>
            <option value="south">South</option>
            <option value="east">East</option>
            <option value="west">West</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-4 sm:gap-6">
        {salesStats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1 pr-3">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 break-words">{stat.title}</p>
                  <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mt-1 break-words">{stat.value}</p>
                  <p className={`text-xs sm:text-sm ${stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'} mt-1 break-words`}>
                    {stat.change} from last period
                  </p>
                </div>
                <div className={`p-2 sm:p-3 rounded-full ${stat.color} flex-shrink-0`}>
                  <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Recent Activities */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Recent Activities</h2>
            <Button
              variant="outline"
              onClick={() => navigate('/lead_activities')}
              className="text-sm"
            >
              View All
            </Button>
          </div>

          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                <div className={`p-2 rounded-full ${activity.type === 'lead' ? 'bg-blue-100' :
                  activity.type === 'opportunity' ? 'bg-green-100' :
                    activity.type === 'deal' ? 'bg-purple-100' :
                      'bg-orange-100'
                  }`}>
                  {activity.type === 'lead' && <Users className="h-4 w-4 text-blue-600" />}
                  {activity.type === 'opportunity' && <Target className="h-4 w-4 text-green-600" />}
                  {activity.type === 'deal' && <CheckCircle className="h-4 w-4 text-purple-600" />}
                  {activity.type === 'followup' && <Clock className="h-4 w-4 text-orange-600" />}
                </div>

                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{activity.title}</h3>
                  <p className="text-sm text-gray-600">{activity.description}</p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>

                <div className="text-right">
                  <p className="font-semibold text-gray-900">{activity.value}</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${activity.status === 'new' ? 'bg-blue-100 text-blue-800' :
                    activity.status === 'proposal' ? 'bg-yellow-100 text-yellow-800' :
                      activity.status === 'closed' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                    }`}>
                    {activity.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Performers */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Performers</h2>

          <div className="space-y-4">
            {topPerformers.map((performer, index) => (
              <div key={index} className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">
                      {performer.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                </div>

                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{performer.name}</h3>
                  <p className="text-sm text-gray-600">{performer.role}</p>
                  <div className="mt-2">
                    <div className="flex justify-between text-sm">
                      <span>Target: ₹{performer.target.toLocaleString()}</span>
                      <span className="font-medium">{performer.achievement}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${performer.achievement}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Quick Actions</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {quickActions.map((action, index) => {
            const IconComponent = action.icon;
            return (
              <button
                key={index}
                onClick={action.onClick}
                className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className={`p-2 sm:p-3 rounded-full ${action.color} flex-shrink-0`}>
                  <IconComponent className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div className="text-left min-w-0 flex-1">
                  <h3 className="text-sm sm:text-base font-medium text-gray-900 break-words">{action.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 break-words mt-1">{action.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CRMSalesDashboard;